import React from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Skeleton,
  Tooltip,
  TextField,
  InputAdornment,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import type { Template } from "./types";
import { formatDate } from "./utils";

interface TemplatesTableProps {
  templates: Template[];
  filteredTemplates: Template[];
  paginatedTemplates: Template[];
  isLoading: boolean;
  page: number;
  rowsPerPage: number;
  templateSearch: string;
  editingTemplateId: string | null;
  editingName: string;
  onSearchChange: (value: string) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  onRowsPerPageChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onRefresh: () => void;
  onPreview: (template: Template) => void;
  onDownload: (template: Template) => void;
  onDelete: (template: Template) => void;
  onStartEditing: (template: Template) => void;
  onEditingNameChange: (value: string) => void;
  onRename: (template: Template) => void;
  onCancelEditing: () => void;
}

const TemplatesTable: React.FC<TemplatesTableProps> = ({
  templates,
  filteredTemplates,
  paginatedTemplates,
  isLoading,
  page,
  rowsPerPage,
  templateSearch,
  editingTemplateId,
  editingName,
  onSearchChange,
  onPageChange,
  onRowsPerPageChange,
  onRefresh,
  onPreview,
  onDownload,
  onDelete,
  onStartEditing,
  onEditingNameChange,
  onRename,
  onCancelEditing,
}) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: "background.paper",
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 1,
              fontWeight: "bold",
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
            }}
          >
            Uploaded Templates
          </Typography>
          <TextField
            size="small"
            placeholder="Search templates..."
            value={templateSearch}
            onChange={(e) => onSearchChange(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ flex: 1, maxWidth: 400 }}
          />
        </Box>
        <Tooltip title="Refresh">
          <IconButton
            onClick={onRefresh}
            disabled={isLoading}
            aria-label="Refresh templates list"
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <TableContainer
        sx={{
          width: "100%",
          position: "relative",
          overflowX: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>File Name</strong>
              </TableCell>
              <TableCell>
                <strong>Uploaded By</strong>
              </TableCell>
              <TableCell>
                <strong>Uploaded Date</strong>
              </TableCell>
              <TableCell align="center">
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Skeleton
                      variant="text"
                      width={`${60 + (index % 3) * 15}%`}
                    />
                  </TableCell>
                  <TableCell>
                    <Skeleton
                      variant="text"
                      width={`${50 + (index % 2) * 20}%`}
                    />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={100} />
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 0.5,
                      }}
                    >
                      <Skeleton variant="circular" width={28} height={28} />
                      <Skeleton variant="circular" width={28} height={28} />
                      <Skeleton variant="circular" width={28} height={28} />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    No templates uploaded yet. Upload your first template to get
                    started!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedTemplates.map((template, index) => {
                const rowKey =
                  template.id ||
                  template.templateId ||
                  template.fileName ||
                  template.name ||
                  `template-${index}`;

                return (
                  <TableRow key={rowKey} hover>
                    <TableCell>
                      {editingTemplateId === template.id ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <input
                            autoFocus
                            aria-label={`Rename template: ${template.fileName || template.name || "template"}`}
                            value={editingName}
                            onChange={(e) =>
                              onEditingNameChange(e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") onRename(template);
                              if (e.key === "Escape") onCancelEditing();
                            }}
                            style={{
                              fontSize: "0.875rem",
                              padding: "4px 8px",
                              border: "1px solid",
                              borderColor: theme.palette.divider,
                              borderRadius: 4,
                              outline: "none",
                              width: "100%",
                              fontFamily: "inherit",
                            }}
                          />
                          <IconButton
                            size="small"
                            aria-label={`Save rename: ${template.fileName || template.name || "template"}`}
                            onClick={() => onRename(template)}
                            color="success"
                          >
                            <CheckIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label={`Cancel rename: ${template.fileName || template.name || "template"}`}
                            onClick={onCancelEditing}
                          >
                            <CloseIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {template.fileName || template.name || "N/A"}
                          <IconButton
                            size="small"
                            aria-label={`Edit name: ${template.fileName || template.name || "template"}`}
                            onClick={() => onStartEditing(template)}
                            sx={{
                              opacity: 0.4,
                              "&:hover": { opacity: 1 },
                            }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>{template.uploadedBy || "N/A"}</TableCell>
                    <TableCell>{formatDate(template.uploadedDate)}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Preview">
                        <IconButton
                          aria-label={`Preview template: ${template.fileName || template.name || "template"}`}
                          onClick={() => onPreview(template)}
                          size="small"
                          color="primary"
                        >
                          <PreviewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton
                          aria-label={`Download template: ${template.fileName || template.name || "template"}`}
                          onClick={() => onDownload(template)}
                          size="small"
                          color="primary"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          aria-label={`Delete template: ${template.fileName || template.name || "template"}`}
                          onClick={() => onDelete(template)}
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredTemplates.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </TableContainer>
    </Paper>
  );
};

export default TemplatesTable;
